import React from "react";
import { motion } from "framer-motion";
import {
    GraduationCap,
    BookOpen,
    BarChart3,
    Users,
    Layers,
    CheckCircle2,
} from "lucide-react";

const WhyChooseUs = () => {
    // Clean, professional fade-up animation
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    const features = [
        {
            title: "Elite Expert Faculty",
            description:
                "Learn directly from seasoned educators, retired bureaucrats, and subject matter experts with proven track records in producing top-tier ranks.",
            icon: <GraduationCap size={24} />,
        },
        {
            title: "Structured Curriculum",
            description:
                "A meticulously planned syllabus tailored for UPSC and State PSCs, completely eliminating guesswork from your daily preparation.",
            icon: <BookOpen size={24} />,
        },
        {
            title: "1-on-1 Mentorship",
            description:
                "Strategic guidance to navigate psychological pressure, maintain consistency, and stay entirely focused on your ultimate goals.",
            icon: <Users size={24} />,
        },
        {
            title: "Advanced Analytics",
            description:
                "Identify micro-weaknesses instantly with our AI-driven test performance metrics and actionable insights.",
            icon: <BarChart3 size={24} />,
        },
    ];

    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white w-full border-t border-gray-200">
            <div className="max-w-7xl mx-auto">
                {/* The Exact Heading & Anchor Block You Selected */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 gap-10">
                    <div className="max-w-2xl">
                        <h2 className="text-4xl md:text-5xl font-black text-neutral-900 leading-snug mb-6">
                            Why{" "}
                            <span className="text-yellow-500">Choose Us.</span>
                        </h2>
                        <div className="w-16 h-1.5 bg-neutral-900 mb-8 rounded-full"></div>
                        <p className="text-gray-600 text-lg leading-relaxed font-medium">
                            We don't just provide study materials; we engineer
                            success. Our platform combines data-driven
                            analytics, expert mentorship, and a battle-tested
                            curriculum built explicitly for India's toughest
                            examinations.
                        </p>
                    </div>

                    {/* Trust Metric / Anchor Block */}
                    <div className="bg-neutral-900 rounded-xl p-8 border border-neutral-800 text-white shadow-xl w-full lg:w-[420px] shrink-0">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center text-neutral-900 shrink-0">
                                <CheckCircle2 size={24} />
                            </div>
                            <div>
                                <h4 className="text-xl font-black">
                                    Proven Methodology
                                </h4>
                                <p className="text-sm font-bold text-gray-400 uppercase">
                                    Results Driven
                                </p>
                            </div>
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed">
                            Our entire ecosystem is designed to minimize
                            distractions and maximize your competitive edge.
                        </p>
                    </div>
                </div>

                {/* Classic 4-Column Grid (Simple & Professional) */}
                <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                >
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            variants={cardVariants}
                            className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 group flex flex-col items-center text-center"
                        >
                            {/* Icon Container (Black & Yellow) */}
                            <div className="w-16 h-16 bg-neutral-900 text-yellow-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300 shadow-md">
                                {feature.icon}
                            </div>

                            {/* Text Content */}
                            <h3 className="text-xl font-bold text-neutral-900 mb-3">
                                {feature.title}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed font-medium">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default WhyChooseUs;
